import { useEffect, useRef } from 'react'
import { useAppDispatch } from '@/utils/typeHelpers'
import type { WidgetDisplay } from '../lib/types'
import { useMerchantCard } from './useMerchantCard'
import {
  fetchMerchantReview,
  handOffMerchant,
  markMerchantPersonal,
  MERCHANT_GONE,
  type CategorizeResult,
  type HandOffResult,
  type MerchantReview,
} from '../merchantReview.slice'

const toSavedSentence = (
  merchant: string,
  result: CategorizeResult
): string => {
  const summary = result.saved
    .map(({ category, count }) => `${count} to ${category}`)
    .join(', ')

  let sentence = `I've set ${merchant}: ${summary}.`

  if (result.lockedCount) {
    sentence += ` Leave the ${result.lockedCount} in closed books alone.`
  }
  if (result.staleCount) {
    sentence += ` ${result.staleCount} had already moved on.`
  }
  if (result.merchantsRemaining) {
    return `${sentence} Take me back to categorizing, ${result.merchantsRemaining} to go.`
  }
  return `${sentence} That was the last merchant.`
}

const allInClosedBooks = (result: HandOffResult): boolean =>
  result.transactionCount === 0 && result.lockedCount > 0

const toHandedOffSentence = (
  merchant: string,
  result: HandOffResult
): string => {
  if (allInClosedBooks(result)) {
    const sentence = `Nothing went to a bookkeeper: all ${result.lockedCount} ${merchant} charges are in closed books, so leave them alone.`
    if (result.merchantsRemaining) {
      return `${sentence} Take me back to categorizing without putting ${merchant} back on screen.`
    }
    return sentence
  }

  let sentence = `I've sent ${merchant} to a bookkeeper with a note.`
  if (result.lockedCount) {
    sentence += ` Leave the ${result.lockedCount} in closed books alone.`
  }
  if (result.merchantsRemaining) {
    return `${sentence} Take me back to categorizing, ${result.merchantsRemaining} to go.`
  }
  return `${sentence} That was the last merchant.`
}

const toHandedOffRecord = (group: MerchantReview): WidgetDisplay => ({
  label: `${group.merchant}: handed to a bookkeeper`,
  meta: group.total,
})

const toPersonalSentence = (
  merchant: string,
  result: HandOffResult | typeof MERCHANT_GONE
): string => {
  if (result === MERCHANT_GONE)
    return `${merchant} was already settled. Take me back to categorizing.`

  if (allInClosedBooks(result)) {
    const sentence = `Nothing was marked personal: all ${result.lockedCount} ${merchant} charges are in closed books, so leave them alone.`
    if (result.merchantsRemaining) {
      return `${sentence} Take me back to categorizing without putting ${merchant} back on screen.`
    }
    return sentence
  }

  let sentence = `I've marked ${merchant} personal.`
  if (result.lockedCount) {
    sentence += ` Leave the ${result.lockedCount} in closed books alone.`
  }
  if (result.merchantsRemaining) {
    return `${sentence} Take me back to categorizing, ${result.merchantsRemaining} to go.`
  }
  return `${sentence} That was the last merchant.`
}

const toPersonalRecord = (
  group: MerchantReview,
  result: HandOffResult | typeof MERCHANT_GONE
): WidgetDisplay | undefined =>
  result === MERCHANT_GONE || allInClosedBooks(result)
    ? undefined
    : { label: `${group.merchant}: marked personal`, meta: group.total }

export interface ConfirmCategory {
  group?: MerchantReview
  groupCategoryId: number | null
  nameOf: (categoryId: number | null) => string
  setGroupCategory: (categoryId: number | null) => void
  confirm: () => Promise<void>
  handOff: (note: string) => Promise<boolean>
  markPersonal: () => Promise<void>
  saving: boolean
}

export const useConfirmCategory = (
  groupKey: string,
  onOutcome: (text: string, display?: WidgetDisplay) => void
): ConfirmCategory => {
  const dispatch = useAppDispatch()
  const card = useMerchantCard(groupKey)
  // State lags a render, so two fast presses would both pass a check on `saving`.
  const sendingRef = useRef(false)

  useEffect(() => {
    if (groupKey) void dispatch(fetchMerchantReview(groupKey))
  }, [dispatch, groupKey])

  return {
    group: card.group,
    groupCategoryId: card.groupCategoryId,
    nameOf: card.nameOf,
    setGroupCategory: card.setGroupCategory,
    saving: card.saving,
    confirm: async (): Promise<void> => {
      if (!card.group) return
      const result = await card.save(() => card.groupCategoryId)
      if (!result) return

      onOutcome(toSavedSentence(card.group.merchant, result))
      card.setSaving(false)
    },
    handOff: async (note: string): Promise<boolean> => {
      const { group } = card
      if (!group || card.saving || sendingRef.current) return false

      sendingRef.current = true
      card.setSaving(true)
      const result = await dispatch(handOffMerchant(group.groupKey, { note }))
      sendingRef.current = false
      if (!result) {
        card.setSaving(false)
        return false
      }

      onOutcome(
        toHandedOffSentence(group.merchant, result),
        allInClosedBooks(result) ? undefined : toHandedOffRecord(group)
      )
      card.setSaving(false)
      return true
    },
    markPersonal: async (): Promise<void> => {
      const { group } = card
      if (!group || card.saving || sendingRef.current) return

      sendingRef.current = true
      card.setSaving(true)
      const result = await dispatch(
        markMerchantPersonal(
          group.groupKey,
          group.transactions.map((transaction) => transaction.id)
        )
      )
      sendingRef.current = false
      if (!result) {
        card.setSaving(false)
        return
      }

      onOutcome(
        toPersonalSentence(group.merchant, result),
        toPersonalRecord(group, result)
      )
      card.setSaving(false)
    },
  }
}
